/* Solde de points Yotpo Loyalty affiché dans le header.
 *
 * POURQUOI CETTE RÉÉCRITURE
 * La version précédente attendait une chaîne de classes exacte
 * (`.yotpo-point-balance` > `.yotpo-point-balance-text` > `.yotpo-point-balance-text-part`)
 * dans le DOM clair, une seule fois, avec un timeout de 15 s — et, en cas
 * d'échec, écrivait `'0'`. Trois conséquences :
 *   1. le moindre changement de markup côté Yotpo (renommage d'une classe,
 *      passage en shadow DOM, widget rendu plus tard que 15 s) casse la lecture ;
 *   2. l'échec est indiscernable d'un vrai solde à zéro, donc on affiche une
 *      information FAUSSE au client au lieu de ne rien affirmer ;
 *   3. le solde ne se met jamais à jour si le client échange des points dans
 *      le tiroir sans recharger la page.
 *
 * STRATÉGIE : plusieurs sources, de la plus fiable à la plus dégradée, et
 * surveillance continue plutôt qu'une tentative unique.
 *   Source 1 — `swellAPI.getCustomerDetails().pointsBalance` : l'API JS
 *     officielle de Yotpo Loyalty (ex-Swell). Ne dépend d'aucun markup.
 *   Source 2 — lecture du DOM, mais par correspondance *partielle* de classe
 *     (`[class*="point-balance"]`) et en traversant les shadow roots ouverts,
 *     donc insensible à un renommage de feuille ou à une encapsulation.
 * Si aucune source ne répond, on n'invente rien : le `--` initial reste, puis
 * le bloc est masqué au bout de HIDE_AFTER_MS pour ne pas laisser un tiret
 * orphelin dans le header.
 */
(function () {
  'use strict';

  var HIDE_AFTER_MS = 20000; // délai avant de masquer le bloc si rien n'est trouvé
  var DEBOUNCE_MS = 200;

  var lastRendered = null;
  var hideTimer = null;

  function blockEl() {
    return document.querySelector('[data-yotpo-points-header]');
  }

  function valueEl() {
    return document.querySelector('[data-yotpo-points-header] [data-yotpo-points-value]');
  }

  /* `points` est un entier, ou null quand on ne sait pas. null ne remplace
   * jamais une valeur déjà affichée : une lecture ratée ne doit pas effacer un
   * solde correct obtenu plus tôt. */
  function render(points) {
    if (points == null || points === lastRendered) return;
    var el = valueEl();
    if (!el) return;
    el.textContent = String(points);
    lastRendered = points;

    var block = blockEl();
    if (block) block.hidden = false;
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  }

  /* ── Source 1 : API JS Yotpo Loyalty ─────────────────────────────────── */

  function fromSwellAPI() {
    var api = window.swellAPI;
    if (!api || typeof api.getCustomerDetails !== 'function') return null;
    try {
      var details = api.getCustomerDetails();
      var balance = details && details.pointsBalance;
      if (typeof balance === 'string') balance = parseInt(balance, 10);
      return typeof balance === 'number' && isFinite(balance) ? balance : null;
    } catch (e) {
      return null; /* API présente mais pas encore initialisée */
    }
  }

  /* ── Source 2 : lecture du DOM (document + shadow roots ouverts) ─────── */

  function collectRoots() {
    var roots = [document];
    var queue = [document];
    /* Garde-fou : un thème + apps peut compter des milliers de noeuds, on
     * borne l'exploration pour ne pas peser sur le thread principal. */
    var visited = 0;
    while (queue.length && visited < 5000) {
      var root = queue.shift();
      var all = root.querySelectorAll('*');
      for (var i = 0; i < all.length && visited < 5000; i++) {
        visited++;
        if (all[i].shadowRoot) {
          roots.push(all[i].shadowRoot);
          queue.push(all[i].shadowRoot);
        }
      }
    }
    return roots;
  }

  /* Le solde peut s'écrire « 1 030 », « 1,030 » ou « 1.030 » selon la locale
   * (l'espace peut être une insécable U+00A0 ou fine U+202F, toutes deux
   * couvertes par \s). On retient le plus grand nombre de l'élément : les
   * libellés voisins (« Gagnez 1 point par euro ») sont plus petits que le
   * solde dans tous les cas réalistes. */
  function largestNumberIn(text) {
    var groups = (text || '').match(/\d[\d\s,.  ]*/g);
    if (!groups) return null;
    var best = null;
    for (var i = 0; i < groups.length; i++) {
      var cleaned = groups[i].replace(/[\s,.  ]/g, '');
      if (!cleaned) continue;
      var num = parseInt(cleaned, 10);
      if (!isNaN(num) && (best === null || num > best)) best = num;
    }
    return best;
  }

  /* Du plus spécifique au plus large : on s'arrête au premier niveau qui
   * donne un nombre, pour ne pas récupérer le texte d'un bloc parent qui
   * engloberait aussi un coût de récompense (« 500 points = 5 € »). */
  var DOM_SELECTORS = [
    '[class*="point-balance-text-part"]',
    '[class*="point-balance-text"]',
    '[class*="point-balance"]',
    '[class*="points-balance"]'
  ];

  function fromDom() {
    var roots = collectRoots();
    for (var s = 0; s < DOM_SELECTORS.length; s++) {
      var best = null;
      for (var r = 0; r < roots.length; r++) {
        var nodes;
        try {
          nodes = roots[r].querySelectorAll(DOM_SELECTORS[s]);
        } catch (e) {
          continue;
        }
        for (var n = 0; n < nodes.length; n++) {
          var num = largestNumberIn(nodes[n].textContent);
          if (num !== null && (best === null || num > best)) best = num;
        }
      }
      if (best !== null) return best;
    }
    return null;
  }

  /* ── Boucle de résolution ────────────────────────────────────────────── */

  function tick() {
    var points = fromSwellAPI();
    if (points === null) points = fromDom();
    render(points);
  }

  var debounceTimer = null;
  function scheduleTick() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(tick, DEBOUNCE_MS);
  }

  function init() {
    if (!valueEl()) return; /* client déconnecté : le snippet rend le CTA */

    tick();

    /* L'observateur reste actif : le tiroir Yotpo monte son DOM à l'ouverture
     * et le solde change après un échange de points, sans rechargement. */
    var observer = new MutationObserver(scheduleTick);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true
    });

    /* `swell:setup` peut avoir déjà été émis quand ce script s'exécute — d'où
     * le tick immédiat ci-dessus en plus de l'écoute. */
    document.addEventListener('swell:setup', scheduleTick);
    window.addEventListener('load', scheduleTick);

    hideTimer = setTimeout(function () {
      if (lastRendered === null) {
        var block = blockEl();
        if (block) block.hidden = true;
      }
    }, HIDE_AFTER_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
